// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { FormFieldClickable, FormFieldIcon } from "@insite/mobius/FormField";
import { IconWrapper } from "@insite/mobius/Icon";
import { FormFieldSizeVariant } from "@vanmeter/MobiusOverrides/FormField";
import { sizeVariantValues } from "@vanmeter/MobiusOverrides/FormField/formStyles";
import { fontConfig } from "@vanmeter/XDL/constants";
import { xBaseTheme } from "@vanmeter/XDL/types";
import { color, ellipsis, getSystemColor, hideEmpty } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

const PlaceholderSelector = `
        &::placeholder,
        &::-webkit-input-placeholder
`;

export const placeholderStyle = css`
    ${PlaceholderSelector} {
        font-size: ${fontConfig.regularSingleLine.size}px;
        line-height: ${fontConfig.regularSingleLine.lineHeight};
        font-weight: ${fontConfig.weight.regular};
        font-style: italic;
        letter-spacing: 0;
        max-width: 100%;
        color: ${getSystemColor("common.ui4")};
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        border: "rounded",
        sizeVariant: "default",
        backgroundColor: "common.ui1",
        labelProps: {
            mergeCss: true,
            size: fontConfig.xSmallSingleLine.size,
            transform: "capitalize",
            weight: fontConfig.weight.regular,
            css: css`
                min-height: initial;
                letter-spacing: 0;
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                display: flex;
                align-items: center;
                & .hint-wrapper {
                    margin-top: 1px;
                    display: flex;
                    flex-grow: 1;
                }
            `,
        },
        errorProps: {
            mergeCss: true,
            size: fontConfig.small.size,
            weight: fontConfig.weight.regular,
            color: "danger",
            css: css`
                min-height: initial;
                letter-spacing: 0;
                line-height: ${fontConfig.smallSingleLine.lineHeight};
            `,
        },
        hintProps: {
            mergeCss: true,
            size: fontConfig.small.size,
            weight: fontConfig.weight.regular,
            color: "text",
            css: css`
                min-height: initial;
                letter-spacing: 0;
                line-height: ${fontConfig.smallSingleLine.lineHeight};
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                width: ${({ labelPosition }: any) => (labelPosition === "left" ? "auto" : "100%")};
                input,
                select,
                textarea {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        ${placeholderStyle}
                        // XNG-Changes: hide placeholder while field is focused
                        &:focus {
                            ${PlaceholderSelector} {
                                opacity: 0;
                            }
                        }
                        &:not(:disabled):not(:focus):hover {
                            border: 1px solid ${color("common.ui15")};
                        }
                    }
                }
                input:has() {
                    &,
                    &:focus {
                        padding-right: attr(data-padding-right px, "8px");
                    }
                }
                ${FormFieldClickable} {
                    ${FormFieldIcon} {
                        right: 0;
                        background: transparent;
                    }
                    &:not(:disabled):hover {
                        ${FormFieldIcon} {
                            color: ${color("common.ui15")};
                        }
                    }
                }
                input,
                select {
                    &:not(.react-datetime-picker__inputGroup__input) {
                        ${ellipsis(1)}
                        text-overflow: ellipsis;
                    }
                }
                input,
                textarea {
                    box-shadow: inset 0 1px 0 0 ${color("common.black", 0.1)};
                }

                textarea {
                    resize: vertical;
                }

                &[data-size="large"] {
                    && input {
                        padding: 0 16px 0 16px;
                    }

                    && ${IconWrapper} {
                        padding: 10px;
                    }
                }

                .end-adornment {
                    position: absolute;
                    right: 0.8px;
                    top: 50%;
                    transform: translateY(-50%);
                    display: flex;
                    align-items: center;
                }

                .clickable-wrapper {
                    ${hideEmpty}
                    position: absolute;
                    top: 0;
                    right: 0;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 0 8px;
                    max-height: ${({ _sizeVariant }: any) =>
                        `${sizeVariantValues[_sizeVariant as FormFieldSizeVariant].height}px`};

                    & > ${FormFieldClickable} {
                        position: relative;
                        padding: 0;
                        width: 16px;
                        height: 100%;
                        & ${FormFieldIcon} {
                            padding: 0;
                            top: unset;
                            width: auto;
                        }
                    }
                }
            `,
            formField: css`
                ${({ labelPosition }: any) => {
                    if (labelPosition === "left") {
                        return css`
                            align-items: baseline;
                        `;
                    }
                }}

                &[disabled] label {
                    opacity: 0.33;
                }
            `,
        },
    },
} as xBaseTheme["formField"];
